import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { adminCmsAPI, publicCmsAPI } from '../../lib/cmsApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Save,
  Globe,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Linkedin,
  Instagram,
  Twitter,
  Image,
} from 'lucide-react';
import ImagePicker from '../../components/cms/ImagePicker';

const CmsSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    tagline: '',
    phone: '',
    email: '',
    address: '',
    footer_text: '',
    copyright_text: '',
    developer_credit: '',
    developer_url: '',
    social_links: {
      linkedin: '',
      facebook: '',
      twitter: '',
      instagram: '',
    },
    logo_url: '',
    favicon_url: '',
    primary_cta_text: '',
    secondary_cta_text: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: [],
  });

  useEffect(() => {
    if (user?.role !== 'administrateur' && user?.role !== 'employe') {
      navigate('/portail');
      return;
    }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await publicCmsAPI.getSettings();
      setSettings({
        ...settings,
        ...response.data,
        social_links: response.data.social_links || {},
      });
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminCmsAPI.updateSettings(settings);
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSocialChange = (platform, value) => {
    setSettings({
      ...settings,
      social_links: {
        ...settings.social_links,
        [platform]: value,
      },
    });
  };

  const handleKeywordsChange = (text) => {
    const keywords = text.split(',').map(k => k.trim()).filter(k => k);
    setSettings({ ...settings, seo_keywords: keywords });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#F9B438]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="cms-settings">
      {/* Header */}
      <header className="bg-[#1D1D1B] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/cms">
                <Button variant="ghost" size="icon" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-2xl font-bold">Paramètres du site</h1>
                <p className="text-white/60 text-sm">Configuration générale</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Sauvegarder
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Informations générales */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#F9B438]" />
                Informations générales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de l'entreprise</Label>
                <Input
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="PEY Consultants Inc."
                />
              </div>
              <div className="space-y-2">
                <Label>Slogan / Tagline</Label>
                <Textarea
                  value={settings.tagline}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  placeholder="Experts en hydrologie urbaine..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Texte du footer</Label>
                <Textarea
                  value={settings.footer_text}
                  onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                  placeholder="Description courte pour le pied de page..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Texte de copyright</Label>
                <Input
                  value={settings.copyright_text}
                  onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
                  placeholder="© {year} PEY Consultants inc."
                />
                <p className="text-xs text-neutral-500">{'{year}'} sera remplacé par l'année en cours</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Crédit développeur</Label>
                  <Input
                    value={settings.developer_credit}
                    onChange={(e) => setSettings({ ...settings, developer_credit: e.target.value })}
                    placeholder="DG Solutions"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL développeur</Label>
                  <Input
                    value={settings.developer_url}
                    onChange={(e) => setSettings({ ...settings, developer_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Coordonnées */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#F9B438]" />
                Coordonnées
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Courriel</Label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-neutral-400" />
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    placeholder="info@peyconsultants.ca"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-neutral-400" />
                  <Input
                    value={settings.phone || ''}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    placeholder="+1 514 555-1234"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Textarea
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  placeholder="Montréal, Québec, Canada"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Réseaux sociaux */}
          <Card>
            <CardHeader>
              <CardTitle>Réseaux sociaux</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <div className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-neutral-400" />
                  <Input
                    value={settings.social_links?.linkedin || ''}
                    onChange={(e) => handleSocialChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/company/..."
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Facebook</Label>
                <div className="flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-neutral-400" />
                  <Input
                    value={settings.social_links?.facebook || ''}
                    onChange={(e) => handleSocialChange('facebook', e.target.value)}
                    placeholder="https://facebook.com/..."
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Twitter / X</Label>
                <div className="flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-neutral-400" />
                  <Input
                    value={settings.social_links?.twitter || ''}
                    onChange={(e) => handleSocialChange('twitter', e.target.value)}
                    placeholder="https://twitter.com/..."
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Instagram</Label>
                <div className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-neutral-400" />
                  <Input
                    value={settings.social_links?.instagram || ''}
                    onChange={(e) => handleSocialChange('instagram', e.target.value)}
                    placeholder="https://instagram.com/..."
                    className="flex-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Images et branding */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-[#F9B438]" />
                Images et branding
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <ImagePicker value={settings.logo_url || ''} onChange={url => setSettings({ ...settings, logo_url: url })} folder="general" label="Logo" />
              </div>
              <div className="space-y-2">
                <ImagePicker value={settings.favicon_url || ''} onChange={url => setSettings({ ...settings, favicon_url: url })} folder="general" label="Favicon" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Texte CTA principal</Label>
                  <Input
                    value={settings.primary_cta_text}
                    onChange={(e) => setSettings({ ...settings, primary_cta_text: e.target.value })}
                    placeholder="Demander une soumission"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texte CTA secondaire</Label>
                  <Input
                    value={settings.secondary_cta_text}
                    onChange={(e) => setSettings({ ...settings, secondary_cta_text: e.target.value })}
                    placeholder="Notre expertise"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SEO */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>SEO - Référencement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titre SEO par défaut</Label>
                  <Input
                    value={settings.seo_title}
                    onChange={(e) => setSettings({ ...settings, seo_title: e.target.value })}
                    placeholder="PEY Consultants - Ingénierie hydraulique"
                  />
                  <p className="text-xs text-neutral-500">{settings.seo_title?.length || 0}/60 caractères recommandés</p>
                </div>
                <div className="space-y-2">
                  <Label>Mots-clés (séparés par des virgules)</Label>
                  <Input
                    value={settings.seo_keywords?.join(', ') || ''}
                    onChange={(e) => handleKeywordsChange(e.target.value)}
                    placeholder="ingénierie, hydrologie, Québec"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Meta description par défaut</Label>
                <Textarea
                  value={settings.seo_description}
                  onChange={(e) => setSettings({ ...settings, seo_description: e.target.value })}
                  placeholder="Description du site pour les moteurs de recherche..."
                  rows={3}
                />
                <p className="text-xs text-neutral-500">{settings.seo_description?.length || 0}/160 caractères recommandés</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button (mobile) */}
        <div className="mt-6 lg:hidden">
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Sauvegarder les paramètres
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CmsSettings;
